import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { defs } from '../content/registry';
import type { CardDef, CardRarity } from '../content/types';
import { createBuildingPreview, createUnitPreview, type PreviewModel } from '../render/modelPreviewRigs';

const ROMAN = ['I', 'II', 'III', 'IV'];

type CardStat = { label: string; value: string };

function cardStats(card: CardDef): CardStat[] {
  const stats: CardStat[] = [];
  if (card.category === 'unit' || card.category === 'boss') {
    const unit = defs.units.get(card.subjectId);
    if (unit) {
      stats.push(
        { label: 'Squad', value: `${unit.squadMemberCount} · ${unit.sizeTag}` },
        { label: 'Health', value: unit.maxHealth.toLocaleString() },
        { label: 'Armor', value: unit.armor },
        { label: 'Speed', value: unit.moveSpeed.toFixed(1) },
      );
      if (unit.attack) stats.push({ label: 'Attack', value: `${unit.attack.damage} / ${unit.attack.period}s · range ${unit.attack.range}` });
    }
  } else if (card.category === 'building') {
    const building = defs.buildings.get(card.subjectId);
    if (building) stats.push(
      { label: 'Health', value: building.maxHealth.toLocaleString() },
      { label: 'Armor', value: building.armor },
      { label: 'Build', value: `${building.buildTime}s` },
      { label: 'Roster', value: building.roster ? `${building.roster.entries.length} units` : '—' },
    );
  } else if (card.category === 'trap') {
    const trap = defs.traps.get(card.subjectId);
    if (trap) stats.push({ label: 'Trigger', value: `${trap.triggerRadius}m` }, { label: 'Pieces', value: `${trap.perDeploymentPieces}` }, { label: 'Build', value: `${trap.buildTime}s` });
  } else if (card.category === 'ritual') {
    const ritual = defs.rituals.get(card.subjectId);
    if (ritual) stats.push({ label: 'Channel', value: `${ritual.channelSeconds}s` }, { label: 'Standard', value: `${ritual.standardHealth} health` });
  } else {
    const spell = defs.spells.get(card.subjectId);
    if (spell) stats.push({ label: 'Target', value: spell.targeting.shape }, { label: 'Duration', value: spell.zoneDuration ? `${spell.zoneDuration}s` : 'Immediate' });
  }
  return stats;
}

function CardFaceStats({ card }: { card: CardDef }) {
  const stats = cardStats(card);
  if (stats.length === 0) return null;
  return (
    <span className="cardface-stats">
      {stats.map((stat) => <span key={stat.label}><small>{stat.label}</small><b>{stat.value}</b></span>)}
    </span>
  );
}

export function rarityOf(card: CardDef): CardRarity {
  if (card.rarity) return card.rarity;
  if (card.category === 'boss' || card.useModel.kind === 'once') return 'legendary';
  if (card.useModel.kind === 'limited') return 'rare';
  if (card.ascension >= 3) return 'uncommon';
  return 'common';
}

export function CardFace({
  card,
  variant = 'pool',
  disabled = false,
  selected = false,
  hotkey,
  footer,
  onClick,
  onHover,
}: {
  card: CardDef;
  variant?: 'compact' | 'pool' | 'full' | 'hover';
  disabled?: boolean;
  selected?: boolean;
  hotkey?: string;
  footer?: React.ReactNode;
  onClick?: () => void;
  onHover?: (hovered: boolean) => void;
}) {
  const rarity = rarityOf(card);
  const move = (event: React.PointerEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    const pointerFromLeft = x + 0.5;
    const pointerFromTop = y + 0.5;
    const pointerFromCenter = Math.min(1, Math.hypot(x, y) * Math.SQRT2);
    event.currentTarget.style.setProperty('--card-rx', `${(-y * 16).toFixed(2)}deg`);
    event.currentTarget.style.setProperty('--card-ry', `${(x * 24).toFixed(2)}deg`);
    event.currentTarget.style.setProperty('--shine-x', `${((x + 0.5) * 100).toFixed(1)}%`);
    event.currentTarget.style.setProperty('--shine-y', `${((y + 0.5) * 100).toFixed(1)}%`);
    event.currentTarget.style.setProperty('--pointer-x', `${(pointerFromLeft * 100).toFixed(1)}%`);
    event.currentTarget.style.setProperty('--pointer-y', `${(pointerFromTop * 100).toFixed(1)}%`);
    event.currentTarget.style.setProperty('--pointer-from-left', pointerFromLeft.toFixed(3));
    event.currentTarget.style.setProperty('--pointer-from-top', pointerFromTop.toFixed(3));
    event.currentTarget.style.setProperty('--pointer-from-center', pointerFromCenter.toFixed(3));
    event.currentTarget.style.setProperty('--background-x', `${(50 + x * 50).toFixed(1)}%`);
    event.currentTarget.style.setProperty('--background-y', `${(50 + y * 50).toFixed(1)}%`);
  };
  const reset = (element: HTMLButtonElement) => {
    element.style.setProperty('--card-rx', '0deg');
    element.style.setProperty('--card-ry', '0deg');
    element.style.setProperty('--shine-x', '50%');
    element.style.setProperty('--shine-y', '50%');
    element.style.setProperty('--pointer-x', '50%');
    element.style.setProperty('--pointer-y', '50%');
    element.style.setProperty('--pointer-from-left', '0.5');
    element.style.setProperty('--pointer-from-top', '0.5');
    element.style.setProperty('--pointer-from-center', '0');
    element.style.setProperty('--background-x', '50%');
    element.style.setProperty('--background-y', '50%');
  };
  return (
    <button
      type="button"
      data-audio="card"
      data-rarity={rarity}
      className={`cardface ${variant} category-${card.category} rarity-${rarity}${selected ? ' selected' : ''}${disabled ? ' disabled' : ''}`}
      disabled={disabled}
      onClick={onClick}
      onPointerMove={move}
      onPointerEnter={() => onHover?.(true)}
      onPointerLeave={(event) => { reset(event.currentTarget); onHover?.(false); }}
      onFocus={() => onHover?.(true)}
      onBlur={() => onHover?.(false)}
      aria-label={`${card.name}, tier ${ROMAN[card.ascension - 1]}`}
    >
      <span className="cardface-sheen" />
      <span className="cardface-foil" />
      <span className="cardface-glitter" />
      <span className="cardface-tier-badge" aria-hidden="true">{ROMAN[card.ascension - 1]}</span>
      <span className="cardface-head">
        <b>{card.name}</b>
        <em>{rarity}</em>
      </span>
      <span className={`cardface-art art-${card.category} fac-${card.faction}`}>
        <span>{card.ui.icon}</span>
      </span>
      <span className="cardface-costs">
        <b>⚡{card.cost.power}</b>
        {card.cost.essence > 0 && <b>✦{card.cost.essence}</b>}
        {(card.cost.command ?? 0) > 0 && <b>⚔{card.cost.command}</b>}
      </span>
      {variant !== 'compact' && variant !== 'hover' && <span className="cardface-type">{card.category} · tier {ROMAN[card.ascension - 1]}</span>}
      {variant !== 'compact' && <span className="cardface-role">{card.ui.roleLine}</span>}
      {(variant === 'full' || variant === 'hover') && <span className="cardface-rules">{card.ui.rulesText}</span>}
      {variant === 'hover' && <CardFaceStats card={card} />}
      {hotkey && <span className="cardface-hotkey">{hotkey}</span>}
      {footer && <span className="cardface-footer">{footer}</span>}
    </button>
  );
}

function EffectPreview(card: CardDef): PreviewModel {
  const group = new THREE.Group();
  const color = card.faction === 'iron-covenant' ? 0xe2b85e
    : card.faction === 'ashen-host' ? 0x9b5bd1
      : card.faction === 'verdant-wilds' ? 0x70c85b : 0x5ddff2;
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(0.85, 0.08, 8, 40),
    new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 1.4, roughness: 0.25 }),
  );
  ring.rotation.x = Math.PI / 2;
  group.add(ring);
  for (let i = 0; i < 5; i++) {
    const shard = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.18 + i * 0.025),
      new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.8, roughness: 0.35 }),
    );
    shard.position.set(Math.cos(i * 1.256) * 0.72, 0.35 + (i % 2) * 0.2, Math.sin(i * 1.256) * 0.72);
    group.add(shard);
  }
  return {
    group,
    animate: (_motion, time) => {
      ring.rotation.z = time * 0.45;
      group.children.slice(1).forEach((child, index) => {
        child.position.y = 0.42 + Math.sin(time * 2 + index) * 0.14;
        child.rotation.y = time + index;
      });
    },
  };
}

export function CardPortrait({ cardId }: { cardId: string }) {
  const hostRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const host = hostRef.current;
    const card = defs.cards.get(cardId);
    if (!host || !card) return;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.12;
    renderer.shadowMap.enabled = true;
    host.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    scene.add(new THREE.HemisphereLight(0xe9f1ff, 0x241b16, 1.8));
    const key = new THREE.DirectionalLight(0xffd9a0, 3);
    key.position.set(4, 7, 6);
    key.castShadow = true;
    scene.add(key);
    let model: PreviewModel;
    if (card.category === 'unit' || card.category === 'boss') model = createUnitPreview(card.subjectId, 0, 1);
    else if (card.category === 'building') model = createBuildingPreview(card.subjectId, 0);
    else model = EffectPreview(card);
    scene.add(model.group);
    const bounds = new THREE.Box3().setFromObject(model.group);
    const size = bounds.getSize(new THREE.Vector3());
    const center = bounds.getCenter(new THREE.Vector3());
    const radius = Math.max(1.2, size.length() * 0.62);
    const camera = new THREE.PerspectiveCamera(34, 1, 0.05, 100);
    camera.position.set(center.x + radius * 0.9, center.y + radius * 0.55, center.z + radius * 1.35);
    camera.lookAt(center);
    const floor = new THREE.Mesh(
      new THREE.CircleGeometry(radius * 1.2, 48),
      new THREE.MeshStandardMaterial({ color: 0x17140f, roughness: 1 }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = bounds.min.y - 0.03;
    floor.receiveShadow = true;
    scene.add(floor);
    const resize = () => {
      const width = Math.max(1, host.clientWidth);
      const height = Math.max(1, host.clientHeight);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(host);
    resize();
    let raf = 0;
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      model.animate('idle', now / 1000, 1 / 60);
      renderer.render(scene, camera);
    };
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      scene.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) return;
        object.geometry.dispose();
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        materials.forEach((material) => material.dispose());
      });
      renderer.dispose();
      // dispose() alone leaves the WebGL context alive until GC. Each inspected card creates one,
      // and browsers cap live contexts (~16) by evicting the oldest — which can be the battle
      // canvas. GameRenderer.dispose() already does this; match it here.
      renderer.forceContextLoss();
      host.removeChild(renderer.domElement);
    };
  }, [cardId]);
  return <div className="cardportrait" ref={hostRef} aria-label="Animated card subject portrait" />;
}

function useModelText(card: CardDef): string {
  if (card.useModel.kind === 'reusable') return `Reusable · ${card.useModel.cooldown}s cooldown`;
  if (card.useModel.kind === 'limited') return `${card.useModel.maxUses} uses · ${card.useModel.cooldown}s cooldown`;
  return 'Once per match';
}

export function CardInspector({ cardId, compact = false }: { cardId: string; compact?: boolean }) {
  const card = defs.cards.get(cardId);
  if (!card) return null;
  const stats: { label: string; value: string }[] = [];
  if (card.category === 'unit' || card.category === 'boss') {
    const unit = defs.units.get(card.subjectId);
    if (unit) {
      stats.push(
        { label: 'Squad', value: `${unit.squadMemberCount} · ${unit.sizeTag}` },
        { label: 'Health', value: unit.maxHealth.toLocaleString() },
        { label: 'Armor', value: unit.armor },
        { label: 'Speed', value: unit.moveSpeed.toFixed(1) },
      );
      if (unit.attack) stats.push({ label: 'Attack', value: `${unit.attack.damage} / ${unit.attack.period}s · range ${unit.attack.range}` });
    }
  } else if (card.category === 'building') {
    const building = defs.buildings.get(card.subjectId);
    if (building) stats.push(
      { label: 'Health', value: building.maxHealth.toLocaleString() },
      { label: 'Armor', value: building.armor },
      { label: 'Build', value: `${building.buildTime}s` },
      { label: 'Roster', value: building.roster ? `${building.roster.entries.length} units` : '—' },
    );
  } else if (card.category === 'trap') {
    const trap = defs.traps.get(card.subjectId);
    if (trap) stats.push({ label: 'Trigger', value: `${trap.triggerRadius}m` }, { label: 'Pieces', value: `${trap.perDeploymentPieces}` }, { label: 'Build', value: `${trap.buildTime}s` });
  } else if (card.category === 'ritual') {
    const ritual = defs.rituals.get(card.subjectId);
    if (ritual) stats.push({ label: 'Channel', value: `${ritual.channelSeconds}s` }, { label: 'Standard', value: `${ritual.standardHealth} health` });
  } else {
    const spell = defs.spells.get(card.subjectId);
    if (spell) stats.push({ label: 'Target', value: spell.targeting.shape }, { label: 'Duration', value: spell.zoneDuration ? `${spell.zoneDuration}s` : 'Immediate' });
  }
  return (
    <section className={`cardinspector category-${card.category}${compact ? ' compact' : ''}`}>
      {!compact && <CardPortrait cardId={cardId} />}
      <div className="cardinspect-copy">
        <div className="cardinspect-kicker">{rarityOf(card)} · {card.category} · Ascension {ROMAN[card.ascension - 1]}</div>
        <h3>{card.name}</h3>
        <strong>{card.ui.roleLine}</strong>
        <p>{card.ui.rulesText}</p>
        <div className="cardinspect-stats">
          {stats.map((stat) => <span key={stat.label}><small>{stat.label}</small><b>{stat.value}</b></span>)}
        </div>
        <div className="cardinspect-foot">⚡{card.cost.power}{card.cost.essence > 0 ? ` · ✦${card.cost.essence}` : ''}{(card.cost.command ?? 0) > 0 ? ` · ⚔${card.cost.command}` : ''} · {useModelText(card)}</div>
      </div>
    </section>
  );
}
